import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import CheatSheet from '../../components/admin/CheatSheet';
import { Shield, AlertTriangle, Download, Loader2, Plus, Trash2, Heart, Phone, Mail, Star, Info, SlidersHorizontal } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { apiClient } from '../../api/client';
import CriticalReviewQueue from '../../components/admin/CriticalReviewQueue';
import ResponderRota from '../../components/admin/ResponderRota';

/* Severity bands — mirror backend GNEP thresholds (empathic_handover._crisis_band) */
const CRISIS_BANDS = [
  { min: 0.95, label: 'CRITICAL', color: '#DC2626', range: '0.95 – 1.00', meaning: 'Explicit high-risk language. Responder alerted immediately (EMERGENCY) and crisis contacts shown in chat; the teen still chooses whether to connect — a declined CRITICAL also alerts the responder.' },
  { min: 0.70, label: 'HIGH', color: '#EA580C', range: '0.70 – 0.94', meaning: 'Strong risk language / multiple high-risk signals. Human contact strongly encouraged.' },
  { min: 0.50, label: 'ELEVATED', color: '#D97706', range: '0.50 – 0.69', meaning: 'Sustained distress. AvA suggested talking to a real person.' },
  { min: 0.35, label: 'MODERATE', color: '#CA8A04', range: '0.35 – 0.49', meaning: 'Repeated distress signals. AvA actively offering support options.' },
  { min: 0.20, label: 'MILD', color: '#65A30D', range: '0.20 – 0.34', meaning: 'Mildly elevated emotional language. Watchful check-in territory.' },
  { min: 0.0, label: 'LOW', color: '#16A34A', range: '0.00 – 0.19', meaning: 'No active risk signals — likely user-initiated handover.' },
];
const crisisBand = (score) => CRISIS_BANDS.find(b => (score || 0) >= b.min) || CRISIS_BANDS[CRISIS_BANDS.length - 1];

const GoldStars = ({ value, size = 14 }) => (
  <span className="inline-flex items-center gap-0.5" title={`${value} / 5`}>
    {[1, 2, 3, 4, 5].map(n => (
      <Star key={n} size={size} fill={value >= n - 0.25 ? '#F5B301' : 'none'} stroke={value >= n - 0.25 ? '#F5B301' : '#C9C2B8'} />
    ))}
  </span>
);

const AdminSafety = () => {
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [domainBreakdown, setDomainBreakdown] = useState([]);
  const [keywords, setKeywords] = useState({});
  const [handoverEvents, setHandoverEvents] = useState([]);
  const [handoverTotal, setHandoverTotal] = useState(0);
  const [handoverBreakdown, setHandoverBreakdown] = useState({});
  // Triggered-but-declined collation (additive; no new tile).
  // Surfaced as a subtitle on the existing Human Handovers card.
  const [declinedTotal, setDeclinedTotal] = useState(0);
  const [triggeredTotal, setTriggeredTotal] = useState(0);
  const [declineRate, setDeclineRate] = useState(0);
  const [ratings, setRatings] = useState(null);
  const [sens, setSens] = useState(null);
  const [sensDraft, setSensDraft] = useState(1.0);
  const [savingSens, setSavingSens] = useState(false);
  const [flags, setFlags] = useState([]);
  const [showLegend, setShowLegend] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('handovers');
  const [newKeyword, setNewKeyword] = useState('');
  const [selectedDomain, setSelectedDomain] = useState('');

  const load = useCallback(async () => {
    try {
      const [evts, kw, handovers, rat, sensRes, flagsRes] = await Promise.all([
        apiClient.request('/api/admin/guardrails/events?limit=50'),
        apiClient.request('/api/admin/guardrails/keywords'),
        apiClient.request('/api/admin/safety/handovers?limit=50'),
        apiClient.request('/api/empathic-handover/admin/ratings').catch(() => null),
        apiClient.request('/api/admin/safety/sensitivity').catch(() => null),
        apiClient.request('/api/admin/safety/trigger-flags').catch(() => null),
      ]);
      setEvents(evts.events || []);
      setTotal(evts.total || 0);
      setDomainBreakdown(evts.domain_breakdown || []);
      setKeywords(kw.domains || {});
      setHandoverEvents(handovers.events || []);
      setHandoverTotal(handovers.total || 0);
      setHandoverBreakdown(handovers.type_breakdown || {});
      setDeclinedTotal(handovers.declined_total || 0);
      setTriggeredTotal(handovers.triggered_total || 0);
      setDeclineRate(handovers.decline_rate || 0);
      if (rat) setRatings(rat);
      if (sensRes) { setSens(sensRes); setSensDraft(sensRes.value); }
      if (flagsRes) setFlags(flagsRes.flags || []);
      if (!selectedDomain && Object.keys(kw.domains || {}).length > 0) setSelectedDomain(Object.keys(kw.domains)[0]);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [selectedDomain]);

  useEffect(() => { load(); }, [load]);

  const addKeyword = async () => {
    if (!newKeyword || !selectedDomain) return;
    await apiClient.request('/api/admin/guardrails/keywords', {
      method: 'POST', body: JSON.stringify({ domain: selectedDomain, action: 'add', keyword: newKeyword }),
    });
    setNewKeyword('');
    load();
  };

  const saveSensitivity = async () => {
    setSavingSens(true);
    try {
      const res = await apiClient.request('/api/admin/safety/sensitivity', {
        method: 'PUT', body: JSON.stringify({ value: sensDraft }),
      });
      setSens(s => ({ ...(s || {}), value: res.value, updated_at: new Date().toISOString(), updated_by: 'you' }));
      setSensDraft(res.value);
    } catch (e) { console.error(e); }
    setSavingSens(false);
  };

  const removeKeyword = async (domain, keyword) => {
    await apiClient.request('/api/admin/guardrails/keywords', {
      method: 'POST', body: JSON.stringify({ domain, action: 'remove', keyword }),
    });
    load();
  };

  const handleExport = async () => {
    const data = await apiClient.request('/api/admin/export/guardrails?format=csv');
    const blob = new Blob([typeof data === 'string' ? data : JSON.stringify(data)], { type: 'text/csv' });
    const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = 'hibff_safety_events.csv'; a.click(); URL.revokeObjectURL(url);
  };

  if (loading) return <AdminLayout pageTitle="Safety & Guardrails">
      <CheatSheet pageId="safety" /><div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-8 h-8 animate-spin" style={{ color: '#F5831F' }} /></div></AdminLayout>;

  const chartData = domainBreakdown.map(d => ({ name: d.domain, count: d.count }));
  const emergencyCount = handoverBreakdown['emergency_override'] || 0;
  const consentCount = handoverBreakdown['consent_based'] || 0;

  return (
    <AdminLayout pageTitle="Safety & Guardrails">
      {/* Top Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="safety-metrics">
        <div className="bg-white p-5 rounded-lg" style={{ border: '1px solid #E0E0E0' }}>
          <p className="text-xs" style={{ color: '#44403A' }}>Guardrail Blocks</p>
          <p className="text-3xl font-bold" style={{ color: '#DC2626' }} data-testid="guardrail-blocks-count">{total}</p>
        </div>
        <div className="bg-white p-5 rounded-lg" style={{ border: '1px solid #E0E0E0' }}>
          <p className="text-xs" style={{ color: '#44403A' }}>Human Handovers</p>
          <p className="text-3xl font-bold" style={{ color: '#F59E0B' }} data-testid="handover-count">{handoverTotal}</p>
          {handoverTotal > 0 && (
            <p className="text-xs mt-1" style={{ color: '#44403A' }}>{emergencyCount} emergency, {consentCount} consent</p>
          )}
          {triggeredTotal > 0 && (
            <p className="text-xs mt-1" style={{ color: '#44403A' }} data-testid="handover-decline-subtitle">
              {declinedTotal} declined · {Math.round(declineRate * 100)}% decline rate
            </p>
          )}
        </div>
        <div className="bg-white p-5 rounded-lg" style={{ border: '1px solid #E0E0E0' }}>
          <p className="text-xs" style={{ color: '#44403A' }}>Emergency Overrides</p>
          <p className="text-3xl font-bold" style={{ color: emergencyCount > 0 ? '#DC2626' : '#1A1209' }} data-testid="emergency-count">{emergencyCount}</p>
        </div>
        <div className="bg-white p-5 rounded-lg" style={{ border: '1px solid #E0E0E0' }}>
          <p className="text-xs" style={{ color: '#44403A' }}>Domains Monitored</p>
          <p className="text-3xl font-bold" style={{ color: '#1A1209' }}>{Object.keys(keywords).length}</p>
        </div>
      </div>

      {/* CRITICAL alert responder rota (Oct 2026) */}
      <div className="mb-6">
        <ResponderRota />
      </div>

      {/* CRITICAL event human-review ledger (founder policy Sep 2026) */}
      <CriticalReviewQueue />

      {/* GNEP Sensitivity dial + 7-day trigger frequency flags */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="bg-white p-5 rounded-lg" style={{ border: '1px solid #E0E0E0' }} data-testid="gnep-sensitivity-card">
          <div className="flex items-center gap-2 mb-1">
            <SlidersHorizontal size={16} style={{ color: '#7B4FD8' }} />
            <h3 className="text-sm font-bold" style={{ color: '#1A1209' }}>GNEP Sensitivity</h3>
          </div>
          <p className="text-xs mb-4" style={{ color: '#6B6257' }}>
            Tunes nudge Levels 1–3 only. Emergency Levels 4–5 and the self-harm keyword floor are locked and never affected.
          </p>
          <div className="flex items-center gap-4">
            <span className="text-xs whitespace-nowrap" style={{ color: '#6B6257' }}>Relaxed 0.50</span>
            <input type="range" min="0.5" max="1.5" step="0.05" value={sensDraft}
              onChange={e => setSensDraft(parseFloat(e.target.value))}
              className="flex-1" style={{ accentColor: '#F5831F' }} data-testid="gnep-sensitivity-slider" />
            <span className="text-xs whitespace-nowrap" style={{ color: '#6B6257' }}>Sensitive 1.50</span>
          </div>
          <div className="flex items-center justify-between mt-4">
            <div>
              <span className="text-3xl font-bold" style={{ color: '#1A1209' }} data-testid="gnep-sensitivity-value">{sensDraft.toFixed(2)}</span>
              {sens && sensDraft !== sens.value && (
                <span className="ml-2 text-xs font-medium" style={{ color: '#D97706' }} data-testid="gnep-sensitivity-unsaved">unsaved (current {sens.value.toFixed(2)})</span>
              )}
            </div>
            <button onClick={saveSensitivity} disabled={savingSens || !sens || sensDraft === sens.value}
              className="px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-40"
              style={{ backgroundColor: '#F5831F' }} data-testid="gnep-sensitivity-save">
              {savingSens ? 'Saving…' : 'Save'}
            </button>
          </div>
          {sens?.updated_at && (
            <p className="text-[11px] mt-2" style={{ color: '#9A9186' }} data-testid="gnep-sensitivity-lastchanged">
              Last changed {sens.updated_at.slice(0, 16).replace('T', ' ')} UTC by {sens.updated_by || '—'} · every change is audit-logged
            </p>
          )}
        </div>

        <div className="bg-white p-5 rounded-lg" style={{ border: '1px solid #E0E0E0' }} data-testid="trigger-flags-card">
          <h3 className="text-sm font-bold mb-1" style={{ color: '#1A1209' }}>7-Day Trigger Frequency</h3>
          <p className="text-xs mb-2" style={{ color: '#6B6257' }}>vs trailing 30-day baseline — red = drifting, use the dial to re-centre</p>
          {flags.length === 0 ? <p className="text-xs" style={{ color: '#9A9186' }}>No data yet</p> : (
            <table className="w-full text-xs">
              <tbody>
                {flags.map(f => (
                  <tr key={f.metric} className="border-t" style={{ borderColor: '#F3F1EC' }} data-testid={`trigger-flag-row-${f.metric}`}>
                    <td className="py-1.5 font-medium" style={{ color: '#44403A' }}>{f.label}</td>
                    <td className="py-1.5 text-right font-mono font-bold" style={{ color: '#1A1209' }}>{f.count_7d}{f.unit === '%' ? '%' : ''}</td>
                    <td className="py-1.5 text-right" style={{ color: '#9A9186' }}>base {f.baseline_daily}{f.unit === '%' ? '%' : '/day'}</td>
                    <td className="py-1.5 text-right">
                      <span title={f.note} className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${f.status === 'red' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`} data-testid={`trigger-flag-status-${f.metric}`}>
                        {f.status === 'red' ? 'DRIFT' : 'OK'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Chart */}
      {chartData.length > 0 && (
        <div className="bg-white p-5 rounded-lg mb-6" style={{ border: '1px solid #E0E0E0' }}>
          <h3 className="text-sm font-bold mb-3" style={{ color: '#1A1209' }}>Blocks by Domain</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="name" tick={{ fontSize: 10 }} /><YAxis /><Tooltip /><Bar dataKey="count" fill="#DC2626" radius={[4, 4, 0, 0]} /></BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 mb-4">
        {['handovers', 'events', 'keywords'].map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`flex-1 py-2 px-3 rounded-md text-sm font-medium capitalize transition-colors ${tab === t ? 'bg-white shadow-sm' : 'text-gray-500'}`}
            style={tab === t ? { color: '#1A1209' } : {}}
            data-testid={`tab-${t}`}
          >
            {t === 'handovers' ? `Handovers (${handoverTotal})` : t === 'events' ? `Guardrail Events (${total})` : 'Keyword Management'}
          </button>
        ))}
      </div>

      {/* Handover Events Tab */}
      {tab === 'handovers' && (
        <>
        {/* How to read these values — same interpretation guide as the safety email */}
        <div className="bg-white rounded-lg mb-4" style={{ border: '1px solid #E0E0E0' }} data-testid="crisis-legend-card">
          <button onClick={() => setShowLegend(!showLegend)} className="w-full p-4 flex items-center justify-between" data-testid="crisis-legend-toggle">
            <div className="flex items-center gap-2">
              <Info size={16} style={{ color: '#7B4FD8' }} />
              <h3 className="text-sm font-bold" style={{ color: '#1A1209' }}>How to Read These Values</h3>
            </div>
            <span className="text-xs" style={{ color: '#6B6257' }}>{showLegend ? 'Hide' : 'Show'}</span>
          </button>
          {showLegend && (
            <div className="px-4 pb-4">
              <p className="text-xs mb-3" style={{ color: '#44403A', lineHeight: 1.6 }}>
                <strong>Crisis Score (0.00–1.00)</strong> is a weighted composite: <strong>35%</strong> AI sentiment analysis, <strong>30%</strong> risk-lexicon matching (self-harm, bullying, extortion, acute fear), <strong>20%</strong> coded-language detection, <strong>15%</strong> behavioural withdrawal. <strong>Nudge Level (0–5)</strong> = how far AvA escalated: 1 gentle check-in · 2 support offered · 3 human suggested · 4 strong encouragement · 5 emergency (consent bypassed). Stepped-care triage aid with C-SSRS-style severity banding — a screening signal, not a clinical diagnosis.
              </p>
              <table className="w-full text-xs" style={{ border: '1px solid #E0E0E0', borderCollapse: 'collapse' }}>
                <thead><tr style={{ backgroundColor: '#F9F9F9' }}>
                  {['Range', 'Band', 'What it means'].map(h => <th key={h} className="text-left p-2 font-medium" style={{ color: '#44403A' }}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {CRISIS_BANDS.slice().reverse().map((b) => (
                    <tr key={b.label} className="border-t" style={{ borderColor: '#EEE' }}>
                      <td className="p-2 font-mono" style={{ color: b.color }}>{b.range}</td>
                      <td className="p-2 font-bold" style={{ color: b.color }}>{b.label}</td>
                      <td className="p-2" style={{ color: '#44403A' }}>{b.meaning}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Human Support Experience — teen star reflections per professional/service */}
        <div className="bg-white rounded-lg mb-4" style={{ border: '1px solid #E0E0E0' }} data-testid="support-experience-card">
          <div className="p-4 flex items-center justify-between border-b" style={{ borderColor: '#E0E0E0' }}>
            <div className="flex items-center gap-2">
              <Star size={16} fill="#F5B301" stroke="#F5B301" />
              <h3 className="text-sm font-bold" style={{ color: '#1A1209' }}>Human Support Experience</h3>
              <span className="text-xs" style={{ color: '#6B6257' }}>Teen reflections after talking to a real human (1–5 gold stars)</span>
            </div>
            {ratings?.total_ratings > 0 && (
              <div className="flex items-center gap-2" data-testid="overall-rating">
                <GoldStars value={ratings.overall_avg || 0} />
                <span className="text-sm font-bold" style={{ color: '#1A1209' }}>{ratings.overall_avg}</span>
                <span className="text-xs" style={{ color: '#6B6257' }}>({ratings.total_ratings} ratings)</span>
              </div>
            )}
          </div>
          {!ratings || ratings.total_ratings === 0 ? (
            <p className="p-6 text-center text-sm" style={{ color: '#44403A' }} data-testid="no-ratings-msg">
              No reflections yet. After a handover, teens see a 5-star meter in their chat to rate the conversation with the human professional — results appear here per service, best to worst.
            </p>
          ) : (
            <div className="p-4">
              <table className="w-full text-sm mb-3">
                <thead><tr style={{ backgroundColor: '#F9F9F9' }}>
                  {['Service / Professional', 'Experience', 'Avg', 'Ratings', 'Last rated'].map(h => <th key={h} className="text-left p-2 text-xs font-medium" style={{ color: '#44403A' }}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {ratings.services.map((s, i) => (
                    <tr key={s.service} className="border-t" style={{ borderColor: '#EEE' }} data-testid={`rating-service-row-${i}`}>
                      <td className="p-2 text-xs font-semibold" style={{ color: '#1A1209' }}>
                        {s.service}
                        {i === 0 && ratings.services.length > 1 && <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ background: '#ECFDF5', color: '#059669' }}>BEST</span>}
                        {i === ratings.services.length - 1 && ratings.services.length > 1 && s.avg_stars < ratings.services[0].avg_stars && <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ background: '#FEF2F2', color: '#DC2626' }}>NEEDS ATTENTION</span>}
                      </td>
                      <td className="p-2"><GoldStars value={s.avg_stars} /></td>
                      <td className="p-2 text-xs font-bold" style={{ color: '#1A1209' }}>{s.avg_stars}</td>
                      <td className="p-2 text-xs" style={{ color: '#44403A' }}>{s.count}</td>
                      <td className="p-2 text-xs" style={{ color: '#6B6257' }}>{s.last_rated?.slice(0, 10) || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {ratings.recent?.some(r => r.comment) && (
                <div>
                  <p className="text-xs font-bold mb-2" style={{ color: '#44403A' }}>Recent comments</p>
                  {ratings.recent.filter(r => r.comment).slice(0, 5).map((r, i) => (
                    <div key={i} className="flex items-start gap-2 py-1.5 border-t" style={{ borderColor: '#F3F1EC' }}>
                      <GoldStars value={r.stars} size={11} />
                      <p className="text-xs flex-1" style={{ color: '#44403A' }}>"{r.comment}" <span style={{ color: '#9A9186' }}>— {r.service}, {r.rated_at?.slice(0, 10)}</span></p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg" style={{ border: '1px solid #E0E0E0' }} data-testid="handover-events-table">
          <div className="p-4 flex items-center justify-between border-b" style={{ borderColor: '#E0E0E0' }}>
            <div className="flex items-center gap-2">
              <Heart size={16} style={{ color: '#DC2626' }} />
              <h3 className="text-sm font-bold" style={{ color: '#1A1209' }}>Safety Handover Events ({handoverTotal})</h3>
            </div>
            <div className="flex items-center gap-3 text-xs" style={{ color: '#666' }}>
              <span className="flex items-center gap-1"><Phone size={12} /> SMS to +44***834</span>
              <span className="flex items-center gap-1"><Mail size={12} /> Email to robert@rparson.co.uk</span>
            </div>
          </div>
          <table className="w-full text-sm">
            <thead><tr style={{ backgroundColor: '#F9F9F9' }}>
              {['Time', 'User (Anonymised)', 'Type', 'Crisis Score', 'Nudge Level', 'Expert'].map(h => <th key={h} className="text-left p-3 text-xs font-medium" style={{ color: '#44403A' }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {handoverEvents.length === 0 ? (
                <tr><td colSpan={6} className="p-8 text-center text-sm" style={{ color: '#44403A' }} data-testid="no-handovers-msg">No safety handover events recorded yet. Events appear here when the empathetic handover system is triggered.</td></tr>
              ) : handoverEvents.map((ev, i) => (
                <tr key={i} className="border-t" style={{ borderColor: '#E0E0E0' }} data-testid={`handover-row-${i}`}>
                  <td className="p-3 text-xs" style={{ color: '#44403A' }}>{ev.timestamp?.slice(0, 16)}</td>
                  <td className="p-3 text-xs font-mono" style={{ color: '#44403A' }}>{ev.user_id?.slice(0, 8)}***</td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${ev.metadata?.is_emergency ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                      {ev.metadata?.is_emergency ? 'Emergency' : 'Consent'}
                    </span>
                  </td>
                  <td className="p-3 text-xs font-bold">
                    <span style={{ color: crisisBand(ev.metadata?.crisis_score).color }}>{(ev.metadata?.crisis_score || 0).toFixed(2)}</span>
                    <span className="ml-1.5 px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ background: `${crisisBand(ev.metadata?.crisis_score).color}1A`, color: crisisBand(ev.metadata?.crisis_score).color }}>
                      {crisisBand(ev.metadata?.crisis_score).label}
                    </span>
                  </td>
                  <td className="p-3 text-xs font-bold" style={{ color: '#1A1209' }}>{ev.metadata?.nudge_level || 0}/5</td>
                  <td className="p-3 text-xs" style={{ color: '#44403A' }}>{ev.metadata?.expert_name || 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}

      {/* Guardrail Events Tab */}
      {tab === 'events' && (
        <div className="bg-white rounded-lg" style={{ border: '1px solid #E0E0E0' }}>
          <div className="p-4 flex items-center justify-between border-b" style={{ borderColor: '#E0E0E0' }}>
            <h3 className="text-sm font-bold" style={{ color: '#1A1209' }}>Guardrail Events ({total})</h3>
            <button onClick={handleExport} className="px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 text-white" style={{ backgroundColor: '#DC2626' }}><Download size={12} /> Export</button>
          </div>
          <table className="w-full text-sm">
            <thead><tr style={{ backgroundColor: '#F9F9F9' }}>
              {['Time', 'User (Anonymised)', 'Domain', 'Direction', 'Preview'].map(h => <th key={h} className="text-left p-3 text-xs font-medium" style={{ color: '#44403A' }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {events.length === 0 ? (
                <tr><td colSpan={5} className="p-8 text-center text-sm" style={{ color: '#44403A' }}>No safety events recorded</td></tr>
              ) : events.map((ev, i) => (
                <tr key={i} className="border-t" style={{ borderColor: '#E0E0E0' }}>
                  <td className="p-3 text-xs" style={{ color: '#44403A' }}>{ev.timestamp?.slice(0, 16)}</td>
                  <td className="p-3 text-xs font-mono" style={{ color: '#44403A' }}>{ev.user_id?.slice(0, 8)}***</td>
                  <td className="p-3"><span className={`px-2 py-0.5 text-xs rounded-full font-medium ${ev.metadata?.domain === 'self_harm' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{ev.metadata?.domain}</span></td>
                  <td className="p-3 text-xs capitalize" style={{ color: '#44403A' }}>{ev.metadata?.direction}</td>
                  <td className="p-3 text-xs truncate max-w-[250px]" style={{ color: '#44403A' }}>{ev.metadata?.message_preview}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Keywords Tab */}
      {tab === 'keywords' && (
        <div className="bg-white p-5 rounded-lg" style={{ border: '1px solid #E0E0E0' }}>
          <div className="flex gap-2 mb-4">
            <select value={selectedDomain} onChange={e => setSelectedDomain(e.target.value)} className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }}>
              {Object.keys(keywords).map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            <input value={newKeyword} onChange={e => setNewKeyword(e.target.value)} placeholder="New keyword..." className="flex-1 px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} onKeyDown={e => e.key === 'Enter' && addKeyword()} />
            <button onClick={addKeyword} className="px-4 py-2 rounded-lg text-sm font-medium text-white flex items-center gap-1.5" style={{ backgroundColor: '#DC2626' }}><Plus size={14} /> Add</button>
          </div>
          {selectedDomain && keywords[selectedDomain] && (
            <div className="flex flex-wrap gap-2">
              {keywords[selectedDomain].map((kw, i) => (
                <span key={i} className="px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 bg-red-50 text-red-700">
                  {kw}
                  <button onClick={() => removeKeyword(selectedDomain, kw)} className="hover:text-red-900"><Trash2 size={12} /></button>
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminSafety;
